import React, { useState, useEffect } from 'react';
import { CalendarDaysIcon, MapPinIcon, ClockIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Event } from '../types';
import { apiService } from '../services/api';

type SortOption = 'closest' | 'furthest' | 'alphabetical';

const Calendar: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [filter, setFilter] = useState<Event['category'] | 'all'>('all');
  const [sortBy, setSortBy] = useState<SortOption>('closest');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsData = await apiService.getEvents();
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      }
    };

    fetchEvents();
  }, []);

  const sortEvents = (events: Event[], sortOption: SortOption): Event[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return [...events].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      switch (sortOption) {
        case 'closest':
          // Sort by closest to today first, then by date ascending
          const diffA = Math.abs(dateA.getTime() - today.getTime());
          const diffB = Math.abs(dateB.getTime() - today.getTime());
          if (diffA !== diffB) {
            return diffA - diffB;
          }
          return dateA.getTime() - dateB.getTime();
          
        case 'furthest':
          // Sort by date descending (furthest first)
          return dateB.getTime() - dateA.getTime();
          
        case 'alphabetical':
          // Sort alphabetically by title
          return a.title.localeCompare(b.title);
          
        default:
          return 0;
      }
    });
  };

  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(event => event.category === filter);
    
  const sortedEvents = sortEvents(filteredEvents, sortBy);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  const getEventStatus = (dateString: string) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'past', label: 'Past Event', color: 'text-gray-500' };
    } else if (diffDays === 0) {
      return { status: 'today', label: 'Today', color: 'text-green-600 font-semibold' };
    } else if (diffDays <= 7) {
      return { status: 'soon', label: `In ${diffDays} day${diffDays === 1 ? '' : 's'}`, color: 'text-orange-600 font-medium' };
    } else {
      return { status: 'upcoming', label: `In ${diffDays} days`, color: 'text-blue-600' };
    }
  };

  const getCategoryColor = (category: Event['category']) => {
    const colors = {
      mass: 'bg-purple-100 text-purple-800 border-purple-200',
      ministry: 'bg-blue-100 text-blue-800 border-blue-200',
      social: 'bg-green-100 text-green-800 border-green-200',
      education: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      special: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[category];
  };

  return (
    <div className="min-h-screen bg-church-light py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Event Calendar
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay up to date with all parish events, Mass times, and community activities.
          </p>
        </header>

        {/* Filter and Sort Controls */}
        <div className="mb-8 space-y-4">
          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-2">
            {['all', 'mass', 'ministry', 'social', 'education', 'special'].map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category as Event['category'] | 'all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2 ${
                  filter === category
                    ? 'bg-church-blue text-white'
                    : 'bg-white text-church-dark hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Sort Options and Event Count */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap justify-center sm:justify-start gap-2">
              <span className="text-sm text-gray-600 self-center mr-2">Sort by:</span>
              {[
                { value: 'closest', label: 'Closest to Today', icon: ChevronUpIcon },
                { value: 'furthest', label: 'Date (Latest First)', icon: ChevronDownIcon },
                { value: 'alphabetical', label: 'Alphabetical', icon: null }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSortBy(option.value as SortOption)}
                  className={`flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2 ${
                    sortBy === option.value
                      ? 'bg-church-gold text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.icon && <option.icon className="h-3 w-3 mr-1" />}
                  {option.label}
                </button>
              ))}
            </div>
            
            {/* Event Count */}
            {sortedEvents.length > 0 && (
              <div className="text-sm text-gray-600 text-center sm:text-right">
                Showing {sortedEvents.length} event{sortedEvents.length === 1 ? '' : 's'}
                {filter !== 'all' && ` in ${filter}`}
              </div>
            )}
          </div>
        </div>

        {/* Events List */}
        <div className="space-y-4">
          {sortedEvents.map((event) => (
            <article
              key={event.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="flex-1 mb-4 md:mb-0 md:mr-6">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-xl font-semibold text-church-dark">
                        {event.title}
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full border ${getCategoryColor(event.category)} ml-4`}
                      >
                        {event.category.charAt(0).toUpperCase() + event.category.slice(1)}
                      </span>
                    </div>

                    <p className="text-gray-600 mb-4">
                      {event.description}
                    </p>

                    <div className="space-y-2 text-sm text-gray-500">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 mr-2 text-church-blue" />
                          <span>{formatDate(event.date)}</span>
                        </div>
                        <span className={`text-xs ${getEventStatus(event.date).color}`}>
                          {getEventStatus(event.date).label}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-2 text-church-blue" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 mr-2 text-church-blue" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    {getEventStatus(event.date).status !== 'past' && (
                      <button 
                        onClick={() => {
                          const addToCalendar = (event: Event) => {
                            // Parse time - handle various formats
                            let timeStr = event.time.split(',')[0].trim();
                            let hour = 12, minute = 0;
                            
                            const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                            if (timeMatch) {
                              hour = parseInt(timeMatch[1]);
                              minute = parseInt(timeMatch[2]);
                              if (timeMatch[3]?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
                              if (timeMatch[3]?.toUpperCase() === 'AM' && hour === 12) hour = 0;
                            }
                            
                            const startDate = new Date(event.date);
                            startDate.setHours(hour, minute, 0, 0);
                            const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
                            
                            const formatDate = (date: Date) => {
                              return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                            };
                            
                            // Create calendar data
                            const calendarData = {
                              title: event.title,
                              start: formatDate(startDate),
                              end: formatDate(endDate),
                              description: event.description,
                              location: event.location
                            };
                            
                            // Try different calendar apps
                            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                            const isAndroid = /Android/.test(navigator.userAgent);
                            
                            if (isIOS) {
                              // iOS Calendar
                              const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//All Saints Church//Event//EN
BEGIN:VEVENT
UID:${Date.now()}@allsaintsthirsk.org.uk
DTSTAMP:${formatDate(new Date())}
DTSTART:${calendarData.start}
DTEND:${calendarData.end}
SUMMARY:${calendarData.title}
DESCRIPTION:${calendarData.description}
LOCATION:${calendarData.location}
END:VEVENT
END:VCALENDAR`;
                              
                              const blob = new Blob([icsContent], { type: 'text/calendar' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } else {
                              // Google Calendar (works on most platforms)
                              const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calendarData.title)}&dates=${calendarData.start}/${calendarData.end}&details=${encodeURIComponent(calendarData.description)}&location=${encodeURIComponent(calendarData.location)}`;
                              window.open(googleUrl, '_blank');
                            }
                          };
                          
                          addToCalendar(event);
                        }}
                        className="bg-church-blue hover:bg-blue-800 text-white font-medium py-2 px-4 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
                      >
                        Add to Calendar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {sortedEvents.length === 0 && (
          <div className="text-center py-12">
            <CalendarDaysIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-500 mb-2">No events found</h3>
            <p className="text-gray-400 mb-4">
              {filter === 'all' 
                ? 'No events are currently scheduled.' 
                : `No ${filter} events are currently scheduled.`
              }
            </p>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="text-church-blue hover:text-blue-800 font-medium underline focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2 rounded"
              >
                View all events
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;